import { handleError } from '@services/errHandler';
import { requestService } from '@services/request';
import { connectionStore } from '@stores/utility-stores/connection';
import { log } from '@utils/log';
import type { DocumentScope } from '@vincerosoft/assessfluence-types';

export type AuditQueueJob = {
  jobId: string;
  tenantId: string;
  databaseId: string;
  collection: string;
  docId: string;
  operation: 'created' | 'updated' | 'deleted';
  actor: {
    uid: string;
    authType: string;
  };
  documentScope?: DocumentScope | null;
  resourceType?: string;
  resourceOwnedBy?: string;
  changes: Record<string, unknown> | null;
  createdAt: string;
  attempts?: number;
  lastError?: string;
};

const AUDIT_QUEUE_DB = 'assessfluence-audit-queue';
const AUDIT_QUEUE_STORE = 'jobs';
const AUDIT_QUEUE_VERSION = 1;

type HttpStatusError = Error & {
  status?: number;
};

function auditQueueFactory() {
  let dbPromise: Promise<IDBDatabase> | null = null;
  let flushInProgress = false;

  function getErrorStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null || !('status' in error)) {
      return undefined;
    }

    const { status } = error as HttpStatusError;
    return typeof status === 'number' ? status : undefined;
  }

  function shouldDiscard(error: unknown) {
    const status = getErrorStatus(error);

    if (status === undefined) {
      return false;
    }

    return status >= 400 && status < 500 && status !== 408;
  }

  function getDb() {
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(AUDIT_QUEUE_DB, AUDIT_QUEUE_VERSION);

        request.onupgradeneeded = () => {
          const db = request.result;

          if (!db.objectStoreNames.contains(AUDIT_QUEUE_STORE)) {
            db.createObjectStore(AUDIT_QUEUE_STORE, {
              keyPath: 'jobId'
            });
          }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }

    return dbPromise;
  }

  async function putJob(job: AuditQueueJob) {
    const db = await getDb();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(AUDIT_QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(AUDIT_QUEUE_STORE);

      store.put(job);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async function getAllJobs() {
    const db = await getDb();

    return new Promise<AuditQueueJob[]>((resolve, reject) => {
      const transaction = db.transaction(AUDIT_QUEUE_STORE, 'readonly');
      const store = transaction.objectStore(AUDIT_QUEUE_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve((request.result || []) as AuditQueueJob[]);
      request.onerror = () => reject(request.error);
    });
  }

  async function deleteJob(jobId: string) {
    const db = await getDb();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(AUDIT_QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(AUDIT_QUEUE_STORE);

      store.delete(jobId);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async function enqueueJob(job: AuditQueueJob) {
    try {
      await putJob({
        ...job,
        attempts: job.attempts || 0,
        lastError: job.lastError || ''
      });

      if (connectionStore.getState().isConnected) {
        await flushPendingJobs();
      }
    } catch (error) {
      handleError(error, 'Failed to enqueue audit queue job');
    }
  }

  async function flushPendingJobs() {
    if (flushInProgress || !connectionStore.getState().isConnected) {
      return;
    }

    flushInProgress = true;

    try {
      const jobs = (await getAllJobs()).sort((left, right) =>
        left.createdAt.localeCompare(right.createdAt)
      );

      for (const job of jobs) {
        try {
          await requestService('/admin/audit/jobs', {
            method: 'POST',
            body: job
          });
          await deleteJob(job.jobId);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown queue error';

          await putJob({
            ...job,
            attempts: (job.attempts || 0) + 1,
            lastError: message
          });

          if (shouldDiscard(error)) {
            log.warn(`Discarding invalid audit queue job ${job.jobId}: ${message}`);
            await deleteJob(job.jobId);
            continue;
          }

          log.warn(`Audit queue flush paused for ${job.jobId}: ${message}`);
          break;
        }
      }
    } finally {
      flushInProgress = false;
    }
  }

  connectionStore.subscribe(
    (state) => state.isConnected,
    async (connected) => {
      if (connected) {
        await flushPendingJobs().catch((error) => {
          handleError(error, 'Failed to flush audit queue on reconnect');
        });
      }
    },
    {
      fireImmediately: true
    }
  );

  return {
    enqueueJob,
    flushPendingJobs
  };
}

export const auditQueueService = auditQueueFactory();
